import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AdminDeletionCancelRequest,
  AdminDeletionList,
  AdminDeletionRequest,
} from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface DeletionRequestsState {
  status: 'loading' | 'ready' | 'error';
  data: AdminDeletionList | null;
  error: ApiError | null;
  reload: () => void;
  request: (body: AdminDeletionRequest) => Promise<void>;
  cancel: (id: number, body?: AdminDeletionCancelRequest) => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** Loads deletion requests. Mutations refetch the list. */
export function useDeletionRequests(): DeletionRequestsState {
  const [status, setStatus] = useState<DeletionRequestsState['status']>('loading');
  const [data, setData] = useState<AdminDeletionList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const api = useTenantApi();
  const started = useRef(false);

  const reload = useCallback(() => {
    setStatus('loading');
    setError(null);
    api.listDeletionRequests().then(
      (next) => {
        setData(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        setData(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api]);

  const refetch = useCallback(async () => {
    const next = await api.listDeletionRequests();
    setData(next);
    setError(null);
    setStatus('ready');
  }, [api]);

  const request = useCallback(
    async (body: AdminDeletionRequest) => {
      await api.requestDeletion(body);
      await refetch();
    },
    [api, refetch],
  );

  const cancel = useCallback(
    async (id: number, body?: AdminDeletionCancelRequest) => {
      await api.cancelDeletion(id, body);
      await refetch();
    },
    [api, refetch],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reload();
  }, [reload]);

  return { status, data, error, reload, request, cancel };
}
